import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { DemoSelector } from "./demos";
import { AnimatePresence } from "framer-motion";
import { AppProvider } from "./context/AppContext";
import { AuthProvider } from "./context/AuthContext";
import { BrandProvider } from "./context/BrandContext";
import { CopyProvider } from "./context/CopyContext";
import TestModeBar from "./components/TestModeBar";
import ADAGuidelines from "./components/ADAGuidelines";
import WeightDisplayConsole from "./components/WeightDisplayConsole";
import FluctuationWarningConsole from "./components/FluctuationWarningConsole";
import FluctuationWarning from "./components/FluctuationWarning";
import WelcomeView from "./components/views/WelcomeView";
import BeforeStartView from "./components/views/BeforeStartView";
import AddEmptyView from "./components/views/AddEmptyView";
import CheckingEmptyView from "./components/views/CheckingEmptyView";
import EmptyAcceptedView from "./components/views/EmptyAcceptedView";
import PlaceEmptyView from "./components/views/PlaceEmptyView";
import RemoveEmptyView from "./components/views/RemoveEmptyView";
import EmailCaptureView from "./components/views/EmailCaptureView";
import MembershipSelectionView from "./components/views/MembershipSelectionView";
import RewardMethodSelectionView from "./components/views/RewardMethodSelectionView";
import SummaryView from "./components/views/SummaryView";
import LegalView from "./components/views/LegalView";
import OfferView from "./components/views/OfferView";
import ConfirmationView from "./components/views/ConfirmationView";
import MemberWelcomeView from "./components/views/MemberWelcomeView";
import SignInView from "./components/views/SignInView";
import SignUpView from "./components/views/SignUpView";
import KioskSignInView from "./components/views/KioskSignInView";
import KioskSignUpView from "./components/views/KioskSignUpView";
import ProductRatingView from "./components/views/ProductRatingView";
import ProductAspectSelectionView from "./components/views/ProductAspectSelectionView";
import PurchaseReasonSelectionView from "./components/views/PurchaseReasonSelectionView";
import RateReturnsView from "./components/views/RateReturnsView";
import DebugCameraView from "./components/views/DebugCameraView";
import MultiObjectDetectionView from "./components/views/MultiObjectDetectionView";
import MultipleItemsDetectedView from "./components/views/MultipleItemsDetectedView";
import VoiceAssistantView from "./components/views/VoiceAssistantView";
import LululemonDemoView from "./components/views/LululemonDemoView";
import LululemonTakebackWelcomeView from "./components/views/LululemonTakebackWelcomeView";
import LululemonBeforeStartView from "./components/views/LululemonBeforeStartView";
import LululemonScanView from "./components/views/LululemonScanView";
import LululemonOfferView from "./components/views/LululemonOfferView";
import LululemonConfirmationView from "./components/views/LululemonConfirmationView";
import NikeDemoView from "./components/views/NikeDemoView";
import NikeTakebackWelcomeView from "./components/views/NikeTakebackWelcomeView";
import NikeBeforeStartView from "./components/views/NikeBeforeStartView";
import NikeScanView from "./components/views/NikeScanView";
import NikeOfferView from "./components/views/NikeOfferView";
import NikeConfirmationView from "./components/views/NikeConfirmationView";
import AdidasDemoView from "./components/views/AdidasDemoView";
import AdidasTakebackWelcomeView from "./components/views/AdidasTakebackWelcomeView";
import AdidasBeforeStartView from "./components/views/AdidasBeforeStartView";
import AdidasScanView from "./components/views/AdidasScanView";
import AdidasOfferView from "./components/views/AdidasOfferView";
import AdidasConfirmationView from "./components/views/AdidasConfirmationView";
import DemoBrandDemoView from "./components/views/DemoBrandDemoView";
import DemoBrandTakebackWelcomeView from "./components/views/DemoBrandTakebackWelcomeView";
import DemoBrandBeforeStartView from "./components/views/DemoBrandBeforeStartView";
import DemoBrandScanView from "./components/views/DemoBrandScanView";
import DemoBrandOfferView from "./components/views/DemoBrandOfferView";
import DemoBrandConfirmationView from "./components/views/DemoBrandConfirmationView";
// Nespresso Demo - isolated module with its own context
import {
  NespressoProvider,
  DemoView as NespressoDemoView,
  WelcomeView as NespressoTakebackWelcomeView,
  BeforeStartView as NespressoBeforeStartView,
  ScanView as NespressoScanView,
  RewardSelectionView as NespressoRewardSelectionView,
  SummaryView as NespressoSummaryView,
  OfferView as NespressoOfferView,
  ConfirmationView as NespressoConfirmationView,
  ShareableAchievementView as NespressoShareView,
  VideoShareView as NespressoVideoShareView,
  DemoControls,
  MilestoneCelebration,
} from "./demos/nespresso";

// Smart root route - shows WelcomeView if brand param exists, otherwise redirects to demos
const RootRoute: React.FC = () => {
  const hasBrand = new URLSearchParams(window.location.search).has('brand');
  if (hasBrand) {
    return <WelcomeView />;
  }
  return <Navigate to="/demos" replace />;
};

function AppContent() {
  const location = useLocation();

  return (
    <div className="relative min-h-screen">
      {/* Global Test Mode Bar */}
      <TestModeBar />

      {/* Global ADA Guidelines */}
      <ADAGuidelines />

      {/* Global Weight Display - console logs only */}
      <WeightDisplayConsole />

      {/* Global Fluctuation Warning - console logs only */}
      <FluctuationWarningConsole />

      {/* Global Fluctuation Warning Pop-up - visual alert */}
      <FluctuationWarning />

      {/* Demo Controls - gamification demo panel (Nespresso) */}
      <DemoControls />

      {/* Milestone Celebration Modal (Nespresso) */}
      <MilestoneCelebration />

      {/* Main Content */}
      <div className="min-h-screen">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Staff Demo Selector - shows all available demos */}
            <Route path="/demos" element={<DemoSelector />} />
            {/* Root: shows legacy WelcomeView if brand param, otherwise redirects to demo selector */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/before-start" element={<BeforeStartView />} />
            <Route path="/add-empty" element={<AddEmptyView />} />
            <Route path="/checking-empty" element={<CheckingEmptyView />} />
            <Route path="/empty-accepted" element={<EmptyAcceptedView />} />
            <Route path="/place-empty" element={<PlaceEmptyView />} />
            <Route path="/remove-empty" element={<RemoveEmptyView />} />
            <Route path="/email-capture" element={<EmailCaptureView />} />
            <Route
              path="/membership-selection"
              element={<MembershipSelectionView />}
            />
            <Route
              path="/reward-method-selection"
              element={<RewardMethodSelectionView />}
            />
            <Route path="/summary" element={<SummaryView />} />
            <Route path="/legal" element={<LegalView />} />
            <Route path="/rate-returns" element={<RateReturnsView />} />
            <Route path="/product-rating" element={<ProductRatingView />} />
            <Route
              path="/product-aspect-selection"
              element={<ProductAspectSelectionView />}
            />
            <Route
              path="/purchase-reason-selection"
              element={<PurchaseReasonSelectionView />}
            />
            <Route path="/offer" element={<OfferView />} />
            <Route path="/confirmation" element={<ConfirmationView />} />
            <Route path="/member-welcome" element={<MemberWelcomeView />} />
            <Route path="/sign-in" element={<SignInView />} />
            <Route path="/sign-up" element={<SignUpView />} />
            <Route path="/kiosk-sign-in" element={<KioskSignInView />} />
            <Route path="/kiosk-sign-up" element={<KioskSignUpView />} />
            <Route path="/debug-camera" element={<DebugCameraView />} />
            <Route
              path="/multi-object-detection"
              element={<MultiObjectDetectionView />}
            />
            <Route
              path="/multiple-items-detected"
              element={<MultipleItemsDetectedView />}
            />
            <Route path="/voice-assistant" element={<VoiceAssistantView />} />
            <Route path="/lululemon" element={<LululemonDemoView />} />
            {/* Lululemon Like New Takeback Flow */}
            <Route
              path="/lululemon/takeback"
              element={<LululemonTakebackWelcomeView />}
            />
            <Route
              path="/lululemon/before-start"
              element={<LululemonBeforeStartView />}
            />
            <Route path="/lululemon/scan" element={<LululemonScanView />} />
            <Route path="/lululemon/offer" element={<LululemonOfferView />} />
            <Route
              path="/lululemon/confirmation"
              element={<LululemonConfirmationView />}
            />
            {/* Nike Refurbished Takeback Flow */}
            <Route path="/nike" element={<NikeDemoView />} />
            <Route
              path="/nike/takeback"
              element={<NikeTakebackWelcomeView />}
            />
            <Route
              path="/nike/before-start"
              element={<NikeBeforeStartView />}
            />
            <Route path="/nike/scan" element={<NikeScanView />} />
            <Route path="/nike/offer" element={<NikeOfferView />} />
            <Route
              path="/nike/confirmation"
              element={<NikeConfirmationView />}
            />
            {/* Adidas Choose to Give Back Takeback Flow */}
            <Route path="/adidas" element={<AdidasDemoView />} />
            <Route
              path="/adidas/takeback"
              element={<AdidasTakebackWelcomeView />}
            />
            <Route
              path="/adidas/before-start"
              element={<AdidasBeforeStartView />}
            />
            <Route path="/adidas/scan" element={<AdidasScanView />} />
            <Route path="/adidas/offer" element={<AdidasOfferView />} />
            <Route
              path="/adidas/confirmation"
              element={<AdidasConfirmationView />}
            />
            {/* Demo Brand Takeback Flow */}
            <Route path="/demobrand" element={<DemoBrandDemoView />} />
            <Route
              path="/demobrand/takeback"
              element={<DemoBrandTakebackWelcomeView />}
            />
            <Route
              path="/demobrand/before-start"
              element={<DemoBrandBeforeStartView />}
            />
            <Route path="/demobrand/scan" element={<DemoBrandScanView />} />
            <Route path="/demobrand/offer" element={<DemoBrandOfferView />} />
            <Route
              path="/demobrand/confirmation"
              element={<DemoBrandConfirmationView />}
            />
            {/* Nespresso Recycling Flow - wrapped in NespressoProvider for gamification context */}
            {/* Step 0: Attract - Resting screen with rotating attract messages + leaderboard */}
            <Route path="/nespresso" element={<NespressoProvider><NespressoBeforeStartView /></NespressoProvider>} />
            <Route
              path="/nespresso/takeback"
              element={<NespressoProvider><NespressoTakebackWelcomeView /></NespressoProvider>}
            />
            <Route
              path="/nespresso/before-start"
              element={<NespressoProvider><NespressoBeforeStartView /></NespressoProvider>}
            />
            <Route path="/nespresso/scan" element={<NespressoProvider><NespressoScanView /></NespressoProvider>} />
            <Route
              path="/nespresso/reward-selection"
              element={<NespressoProvider><NespressoRewardSelectionView /></NespressoProvider>}
            />
            <Route
              path="/nespresso/summary"
              element={<NespressoProvider><NespressoSummaryView /></NespressoProvider>}
            />
            <Route path="/nespresso/offer" element={<NespressoProvider><NespressoOfferView /></NespressoProvider>} />
            <Route
              path="/nespresso/share"
              element={<NespressoProvider><NespressoVideoShareView /></NespressoProvider>}
            />
            <Route
              path="/nespresso/video"
              element={<NespressoProvider><NespressoVideoShareView /></NespressoProvider>}
            />
            <Route
              path="/nespresso/confirmation"
              element={<NespressoProvider><NespressoConfirmationView /></NespressoProvider>}
            />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrandProvider>
      <AuthProvider>
        <AppProvider>
          <CopyProvider>
            <NespressoProvider>
              <Router>
                <AppContent />
              </Router>
            </NespressoProvider>
          </CopyProvider>
        </AppProvider>
      </AuthProvider>
    </BrandProvider>
  );
}

export default App;
